import { Tag } from '@dfh-corp-sites/ui'
import { cn } from '@dfh-corp-sites/ui/utils'
import React, { ImgHTMLAttributes } from 'react'

import { Maybe } from '../../../graphql/gatsby'
import { SanityContact, SanityImage } from '../../../groq/sanity-dispensaries/types'
import { dispensaryLogoSrcProps } from '../../../lib/dispensaries/logo'
import { dispensaryImageUrlFor } from '../../../lib/sanity'
import { DFHLogo } from '../../global/header/ui'

type Props = {
  specialties?: Maybe<SanityContact['specialties']>
  displayName?: string
  logo?: Maybe<{
    _type: 'logo'
    image: Maybe<SanityImage>
  }>
  image?: Maybe<SanityImage>
}

const heroImageSrcProps = (
  image: SanityImage,
): Pick<ImgHTMLAttributes<HTMLImageElement>, 'sizes' | 'src' | 'srcSet'> => {
  const src282 =
    dispensaryImageUrlFor(image.asset._ref).size(282, 282).fit('max').auto('format').url() || ''
  const src400 =
    dispensaryImageUrlFor(image.asset._ref).size(400, 400).fit('max').auto('format').url() || ''
  const src600 =
    dispensaryImageUrlFor(image.asset._ref).size(600, 600).fit('max').auto('format').url() || ''
  const src800 =
    dispensaryImageUrlFor(image.asset._ref).size(800, 800).fit('max').auto('format').url() || ''
  const src1000 =
    dispensaryImageUrlFor(image.asset._ref).size(1000, 1000).fit('max').auto('format').url() || ''
  const src1200 =
    dispensaryImageUrlFor(image.asset._ref).size(1200, 1200).fit('max').auto('format').url() || ''
  const src1400 =
    dispensaryImageUrlFor(image.asset._ref).size(1400, 1400).fit('max').auto('format').url() || ''
  const src1600 =
    dispensaryImageUrlFor(image.asset._ref).size(1600, 1600).fit('max').auto('format').url() || ''
  return {
    sizes: '(max-width: 767px) 282px, (max-width: 1222px) 45vw, 550px',
    src: src800,
    srcSet: `${src282} 282w, ${src400} 400w, ${src600} 600w, ${src800} 800w, ${src1000} 1000w, ${src1200} 1200w, ${src1400} 1400w, ${src1600} 1600w`,
  }
}

const Hero = ({ displayName, specialties, logo, image }: Props) => {
  return (
    <div className="border-l-4 border-brand-orange">
      <div className={cn('flex flex-col', 'md:flex-row')}>
        <div className={cn('w-full px-10 ', 'md:w-[55%] md:flex md:justify-end')}>
          <div className="w-full  max-w-[655px]">
            <div className="mt-6 md:mt-12">
              {logo?.image?.asset?._ref ? (
                <img
                  className="w-auto h-[90px] object-contain"
                  alt="Dispensary logo"
                  {...dispensaryLogoSrcProps(logo.image)}
                />
              ) : (
                <DFHLogo />
              )}
            </div>

            <div aria-hidden className="h-[1px] w-full bg-brand-rule-grey my-10" />

            <span className="uppercase font-medium">about</span>

            <div className="mt-6">
              <h1
                className={cn('text-brand-navy text-3xl leading-[120%] font-medium', 'md:text-6xl')}
              >
                {displayName}
              </h1>
            </div>

            <div className="mt-12 flex flex-wrap gap-3">
              {specialties?.map((item) => (
                <Tag
                  disabled
                  asChild
                  key={item}
                  size="lg"
                  accent="outlined"
                  variant="category"
                  className="border font-normal"
                >
                  <span>{item}</span>
                </Tag>
              ))}
            </div>
          </div>
        </div>

        {image && (
          <img
            className={cn(
              'w-full h-[282px] bg-white object-contain mt-6',
              'md:w-[45%] md:h-[550px] md:mt-12',
            )}
            {...heroImageSrcProps(image)}
            alt={`${displayName} profile`}
          />
        )}
      </div>
    </div>
  )
}

export { Hero }
